import { upperFirst } from "lodash-es";
import {Service, service, inject} from "./di";
import {ConfigService, RuntimeConfig} from "./ConfigService";
import {AnalyticsService} from "./AnalyticsService";
import {SeoService} from "./SeoService";
import {LocationInfo} from "./LocationService";
import {fetch, fetchJSON, FetchOptions} from "../utils/fetch";
import {https} from "../utils/http";
import {FlyerJSON, ItemJSON, LoyaltyProgramJSON, TermsJSON, StoreInfoJSON, DataJSON,
				EcomItemJSON, EcomItemMediaObj, SearchJSON, DisplayType, MultiEcomItemJSON} from "../definitions/BackflippDefinitions";
import {isChromeHeadless} from "../utils/crawler";

// Private Definitions ------------------------------------------------------->
interface LocationInfoJson {
	postal_code: string;
	city_name: string;
	country_code: string;
	region_name: string;
}
// Public Definitions -------------------------------------------------------->

@service("backflippService")
export class Backflipp extends Service {
	@inject config: ConfigService;
	@inject seoService: SeoService;

	private analytics: AnalyticsService = AnalyticsService.getInstance();

	private get _locale() {
		// Work around backflipp only accepting fr-CA for French.
		const locale = this.seoService.locale || "en";
		return locale === "fr" ? "fr-CA" : locale;
	}

	getLocationInfo(postalCode?: string): Promise<LocationInfo> {
		const url = postalCode ?
				`${this.getBackflippUrl()}/flipp/location_info/by_postal_code?postal_code=${postalCode}` :
				`${this.getBackflippUrl()}/flipp/location_info/by_ip`;

		return fetchJSON<LocationInfo>(url)
				.then((info: LocationInfoJson) => {
					const postal_code = info.postal_code.length === 3 ?
							info.postal_code + "1A1" : info.postal_code;

					if (!info.city_name) {
						const inCity = this.seoService.inCity;
						if (inCity) {
							const city = inCity.split("-");
							city.pop();
							info.city_name = city.map((c: string) => upperFirst(c)).join(" ");
						}
					}

					return {
						postal_code: postal_code,
						city_name: info.city_name,
						country_code: info.country_code,
						region_name: info.region_name
					};
				});
	}

	getStoreInfo(postalCode: string, id: string): Promise<StoreInfoJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		return fetchJSON<StoreInfoJSON>(
				`${this.getBackflippUrl()}/flipp/flyers/${id}/info?locale=${this._locale}&postal_code=${postalCode}${distType}&sid=${this.getSessionId()}`);
	}

	getFlyersData(postalCode: string): Promise<DataJSON> {
		// Pass in extra parameter to backflipp if we're in distribution to they can server appropriate content
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		// Use /png_data API if we're in the P&G Everyday site (to get access correct coupon content)
		const endpoint = this.config.RUNTIME === RuntimeConfig.PG ? "/png_data" : "/data";

		// FIXME [FW-159] The error handling should be caught higher up, so we can
		//       alert the user that they entered an invalid postal code.
		const opts: FetchOptions = {
			url: `${this.getBackflippUrl()}/flipp${endpoint}?locale=${this._locale}&postal_code=${postalCode}&sid=${this.getSessionId()}${distType}`,
		};
		return fetch<DataJSON>(opts).then((json: DataJSON) => {
			json.flyers.forEach((flyer) => {
				flyer.thumbnail_url = https(flyer.thumbnail_url);
				if (flyer.merchant_logo &&
						flyer.merchant_logo !== "/images/bg/logo_bg.gif")
					flyer.merchant_logo = https(flyer.merchant_logo);
				else
					delete flyer.merchant_logo;
			});

			json.coupons.forEach((coupon) => {
				coupon.coupon_image_url = https(coupon.coupon_image_url);
				coupon.barcode_image_url = https(coupon.barcode_image_url);
				coupon.merchant_logo_url = https(coupon.merchant_logo_url);
				coupon.brand_logo_url = https(coupon.brand_logo_url);
			});

			json.flyer_item_coupons.forEach((item) => {
				item.item_cutout_url = https(item.item_cutout_url);
			});

			return json;
		}).catch((error: Error) => {
			return {
				category_sort_csv: "",
				coupon_category_sort_csv: "",
				coupons: [],
				flyer_item_coupons: [],
				flyers: [],
				loyalty_program_coupons: []
			};
		});
	}

	async getFlyer(id: string, postalCode: string, fastFlyer: boolean = false): Promise<FlyerJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";

		// We don't need to pass postal code to backflipp unless 'include_flyer_metadata=1' is requested.
		// const fsa = postalCode ? `&postal_code=${postalCode}` : "";
		let fsa = "";
		if (fastFlyer) {
			fsa = postalCode ? `&postal_code=${postalCode}` : "";
		}

		const includeMeta = fastFlyer ? "&include_flyer_metadata=1" : "";

		// use it only for monitis!!! pass '-1' to queryParams in dist mode to 'flyer_id=-1' to get first flyers
		if (id === "-1" && this.config.RUNTIME === RuntimeConfig.DISTRIBUTION) {
			const data = await this.getFlyersData(postalCode);
			id = data && data.flyers && data.flyers[0].id.toString();
		}

		return fetchJSON<FlyerJSON>(
			`${this.getBackflippUrl()}/flipp/flyers/${id}?locale=${this._locale}${includeMeta}${fsa}${distType}&sid=${this.getSessionId()}`)

			.then((json: FlyerJSON) => {
				json.items.forEach((item) => {
					item.cutout_image_url = https(item.cutout_image_url);
				});

				if (fastFlyer && json.flyer) {
					if (json.items.length > 0 && Object.keys(json.flyer).length === 0 && json.flyer.constructor === Object) {
						// We have seen a case where fast flyer endpoint returns items that
						// are linked to flyer X, but the flyer itself is not returned.
						// Items cannot exist without the flyer. If a flyer is not available
						// in certain FSA, then neither flyer nor items should be returned!
						// Unexpected! Payload has flyer items, BUT the flyer itself is empty!
						// -------------------------------------------------------------------
						return null;
					}

					const flyer = json.flyer;
					flyer.thumbnail_url = https(flyer.thumbnail_url);

					if (flyer.merchant_logo && flyer.merchant_logo !== "/images/bg/logo_bg.gif") {
						flyer.merchant_logo = https(flyer.merchant_logo);
					} else {
						delete flyer.merchant_logo;
					}
				}
				return json;
			}).catch((err: Error) => {
				return null;
			});
	}

	getFlyerItem(id: string, postalCode?: any): Promise<ItemJSON> {
		const prefix = postalCode ? `&postal_code=${postalCode}` : "";
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		const requestUrl = `${this.getBackflippUrl()}/flipp/items/${id}?locale=${this._locale}${distType}&sid=${this.getSessionId()}${prefix}`;
		return fetchJSON<ItemJSON>(requestUrl)
				.then((json: ItemJSON) => {
					if (!json || (<any> json).error) return null;

					json.item.merchant_logo = https(json.item.merchant_logo);
					json.item.cutout_image_url = https(json.item.cutout_image_url);
					json.item.image_url = https(json.item.image_url);
					json.item.ttm_url = json.item.ttm_url &&
						json.item.display_type === DisplayType.IFRAME ?
						https(json.item.ttm_url) : json.item.ttm_url;

					return json;
				}).catch((err: Error) => {
					return null;
				});
	}

	getCouponDetails(ids: string[]): Promise<TermsJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		return fetchJSON<TermsJSON>(
			`${this.getBackflippUrl()}/flipp/coupons?ids=${ids.join(",")}&locale=${this._locale}${distType}&sid=${this.getSessionId()}`);
	}

	getEcomItem(id: string): Promise<EcomItemJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";

		const opts: FetchOptions = {
			url: `${this.getBackflippUrl()}/flipp/ecom_items/${id}?locale=${this._locale}${distType}&sid=${this.getSessionId()}`,
		};

		return fetchJSON<EcomItemJSON>(opts)
		.then((json: EcomItemJSON) => {
			if (!json || (<any> json).error) {
				return null;
			}

			json.ecom_item.merchant_logo = https(json.ecom_item.merchant_logo);

			json.ecom_item.media.forEach((mediaObj: EcomItemMediaObj) => {
				mediaObj.url = https(mediaObj.url);
			});

			return json;
		});
	}

	getMultiEcomItems(ids: string[]): Promise<MultiEcomItemJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";

		const opts: FetchOptions = {
			url: `${this.getBackflippUrl()}/flipp/ecom_items/multi?ids=${ids.join(",")}&locale=${this._locale}${distType}&sid=${this.getSessionId()}`,
		};

		return fetchJSON<MultiEcomItemJSON>(opts)
		.then((json: MultiEcomItemJSON) => {
			if (!json || (<any> json).error) {
				return null;
			}

			return json;
		});
	}

	search(postalCode: string, query: string): Promise<SearchJSON> {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";

		return fetchJSON<SearchJSON>(
				`${this.getBackflippUrl()}/flipp/items/search?locale=${this._locale}&postal_code=${postalCode}&sid=&q=${encodeURIComponent(query)}${distType}`)
				.then((json: SearchJSON) => {
					json.items.forEach((item) => {
						if (item.merchant_image_url &&
								item.merchant_image_url !== "/images/bg/logo_bg.gif")
							item.merchant_image_url = https(item.merchant_image_url);
						else
							delete item.merchant_image_url;
						item.clipping_image_url = https(item.clipping_image_url);
					});

					if (Array.isArray(json.ecom_items) && json.ecom_items.length > 0) {
						json.ecom_items.forEach((ecomItemObj) => {
							ecomItemObj.merchant_logo = https(ecomItemObj.merchant_logo);
							ecomItemObj.image_url = https(ecomItemObj.image_url);
						});
					}

					return json;
				});
	}

	getFlyerCorrectionNoticesURL(id: string) {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		return `${this.getBackflippUrl()}/flipp/flyers/${id}/correction_notices?locale=${this._locale}${distType}&sid=${this.getSessionId()}`;
	}

	getLoyaltyPrograms(postalCode: string) {
		const distType = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "&channel=dist" : "";
		return fetchJSON<LoyaltyProgramJSON>(
			`${this.getBackflippUrl()}/flipp/loyalty_programs?postal_code=${postalCode}${distType}&sid=${this.getSessionId()}`)
				.then((cards: LoyaltyProgramJSON) => {

					if (this.config.RUNTIME === RuntimeConfig.PG) {
						/* For P&G Everyday, we don't want to filter out rebate loyalty programs because we want to at least
						* show them in the UI
						*/
						return cards;
					} else {
						/* Filter our loyalty programs if 'validation_method' is not set (ie rebate programs) See FW-986.
						* Rationale: It’s not as simple as `!is_rebate && is_load_to_card`.  The problem is that there
						* are still “wallet-only” cards and those won’t render.  Ideally there is an `is_wallet` bool
						* that represents whether a card logically makes sense as a card, but in lieu of that, we
						* decided to use `validation_method != null` as a proxy for that bool because it effectively
						* means the same thing.
						* */
						cards.loyalty_programs = cards.loyalty_programs.filter((card) => card.validation_method);
						return cards;
					}
				})
			.catch((err: Error) => {
				return {
					loyalty_programs: [],
					loyalty_program_merchants: []
				} as LoyaltyProgramJSON;
			});
	}

	private getBackflippUrl() {
		return isChromeHeadless() ? this.config.BACKFLIPP_SERVER_PROXY_CACHE : this.config.BACKFLIPP_SERVER;
	}

	private getSessionId() {
		return isChromeHeadless() ? "none" : this.analytics.sessionId;
	}
}
